import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { VERIFICATION_STATUES } from '@/constants';
import * as dataActions from '@/data/data-actions';
import RsModal from '@/widgets/components/rs-modal';
import RsModalBody from '@/widgets/components/rs-modal-body';
import RsModalFooter from '@/widgets/components/rs-modal-footer';
import RsModalHeader from '@/widgets/components/rs-modal-header';
import { Input } from './input-fields/input';
import { TextArea } from './input-fields/textarea';
import {
  CORRECT_LISTING,
  FlaggingActions,
  INCORRECT_LISTING,
} from './modal-modules/flagging-actions';
import {
  Buttons,
  CloseButton,
  Inputs,
  SendButton,
  Text,
} from './verification-modal-styles';

// Value is always of type string as we cast it to a string via "handleUserInput"
function valueOrUndefined(value) {
  const trimmedValue = value.trim();
  if (trimmedValue.length) return trimmedValue;
  return undefined;
}

const StyledRsModalHeader = styled(RsModalHeader)`
  font-size: 20px;
`;

const StyledRsModalBody = styled(RsModalBody)`
  padding: 0;
  margin: 0;
  margin-top: 20px;
`;

export function ListingVerificationModal(props) {
  const {
    modalTitle,
    listingIsUnidentified,
    listing: {
      verifiedStatus: listingVerifiedStatus,
      id: listingId,
      deckardid: listingDeckardId,
    },
    propertyData: {
      apn,
      address: propertyAddress,
      deckardid: propertyDeckardId,
    },
  } = props;

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  // State will reset upon popover close, because of "speaker" behavior of "Whisper" component
  const [flaggingAction, setFlaggingAction] = useState(null);

  // Suggested/Corrected Listing Info States
  const [correctedAPN, setCorrectedAPN] = useState('');
  const [correctedAddress, setCorrectedAddress] = useState('');
  const [description, setDescription] = useState('');

  const handleRadioSelect = useCallback((e) => {
    setFlaggingAction(e.target.value);
  }, []);

  const listingIsVerified =
    listingVerifiedStatus === VERIFICATION_STATUES.VERIFIED;
  const listingIsUnverified =
    listingVerifiedStatus === VERIFICATION_STATUES.UNVERIFIED;

  // An "unknown" listing that is both unverified and unidentified (no probable apn and address)
  const listingIsUnknown = listingIsUnverified && listingIsUnidentified;
  const isUnverifiedKnownListing =
    listingIsUnverified && !listingIsUnidentified;

  const showField = useMemo(
    () =>
      // Verified listings can always be disputed.
      listingIsVerified ||
      // User believes that the listing has been attributed to the incorrect property.
      flaggingAction === INCORRECT_LISTING,
    [listingIsVerified, flaggingAction]
  );

  const displayButtons = useMemo(
    () =>
      listingIsVerified ||
      listingIsUnknown || // Verified and "unknown" listings do not need an accompanying action
      /**
       * Unverified listings need to have an accompanying action.
       * Actions can only be selected when the listing is unverified, see "isUnverifiedKnownListing" and "FlaggingActions"
       */
      (listingIsUnverified && Boolean(flaggingAction)),
    [listingIsVerified, listingIsUnknown, listingIsUnverified, flaggingAction]
  );

  const verifyListing = useCallback(async () => {
    setIsLoading(true);

    // See "userVerifiedListingSaga" for additional payload logics
    dispatch(
      dataActions.userVerifiedListing({
        /**
         * The status can only be verified when:
         *  1. The listing is unverified (button displays 'Verify')
         *  2. The user selects the first option ("Correct APN/Address for this listing")
         */
        isVerifying: listingIsUnverified && flaggingAction === CORRECT_LISTING,

        // Property information
        propertyDeckardID: propertyDeckardId,

        // Listing information
        listingId,
        listingDeckardId,
        misc: {},

        apn,
        address: propertyAddress,
        suggestedAPN: valueOrUndefined(correctedAPN),
        suggestedSiteAddress: valueOrUndefined(correctedAddress),
        description: valueOrUndefined(description),
      })
    );

    setIsLoading(false);
    props.onClose();
  }, [
    apn,
    correctedAPN,
    correctedAddress,
    description,
    dispatch,
    listingDeckardId,
    listingId,
    flaggingAction,
    listingIsUnverified,
    propertyAddress,
    propertyDeckardId,
    props,
  ]);

  const handleUserInput = useCallback(
    (setFn) => (e) => setFn(String(e.target.value).trimStart()),
    []
  );

  return (
    <RsModal open={props.show} onClose={props.onClose}>
      <StyledRsModalHeader>{modalTitle}</StyledRsModalHeader>

      <StyledRsModalBody>
        <Text>Please provide your assessment of the listing.</Text>

        <FlaggingActions
          allowUserToSelectAction={isUnverifiedKnownListing}
          handleRadioSelect={handleRadioSelect}
          flaggingAction={flaggingAction}
        />

        <Inputs>
          {showField && (
            <Input
              id="correctAPN"
              label="Correct APN (if known)"
              value={correctedAPN}
              onChange={handleUserInput(setCorrectedAPN)}
              maxLength={100}
            />
          )}

          {(showField || listingIsUnknown) && (
            <Input
              id="correctAddress"
              label={
                listingIsUnknown ? 'Address' : 'Correct Address (if known)'
              }
              value={correctedAddress}
              onChange={handleUserInput(setCorrectedAddress)}
              maxLength={2000}
            />
          )}

          {(showField || listingIsUnknown) && (
            <TextArea
              label="Please provide any additional information you think is necessary."
              id="verificationDescription"
              value={description}
              onChange={handleUserInput(setDescription)}
            />
          )}
        </Inputs>
      </StyledRsModalBody>

      {displayButtons && (
        <RsModalFooter>
          <Buttons>
            <SendButton
              onClick={verifyListing}
              loading={isLoading}
              disabled={isLoading}
            >
              Send
            </SendButton>
            <CloseButton onClick={props.onClose} disabled={isLoading}>
              Close
            </CloseButton>
          </Buttons>
        </RsModalFooter>
      )}
    </RsModal>
  );
}

ListingVerificationModal.propTypes = {
  modalTitle: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  propertyData: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  listingIsUnidentified: PropTypes.bool.isRequired,
};
